import styles from "./welcome.module.css";
import { Typography } from "@mui/material";
import i18nPackage from "../../i18n/i18n";
import { CoreButton, CoreSkeletonWrapper, VerityColorLogo } from "../../core";
import BeforeQuizImage from "../../images/before-quiz-image.svg?react";
import { getBeforeQuizTitle } from "../before-quiz";
import { ComponentPropsWithoutRef } from "react";
import { CoreButtonProps } from "../../core/button/Button";

export type WelcomeStepProps = {
	title?: React.ReactNode;
	quizName?: string;
	courseName?: string;
	onStart?: () => void;
	showSkeleton?: boolean;
	/**@default true */
	showDescription?: boolean;
	/**@default true */
	showTitle?: boolean;
	/**@default true */
	showImage?: boolean;
	mainComponent?: React.ReactNode;
	/**@default false */
	isCompatibilityCheck?: boolean;
	slotProps?: {
		container?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		quizInfoContainer?: ComponentPropsWithoutRef<"div">;
		startButton?: Omit<CoreButtonProps, "onClick" | "children">;
	};
};
export default function WelcomeStep({
	title,
	quizName,
	courseName,
	onStart,
	showSkeleton = false,
	mainComponent,
	isCompatibilityCheck = false,
	showDescription = true,
	showTitle = true,
	showImage = true,
	slotProps,
}: WelcomeStepProps) {
	const renderContent = () => {
		if (showSkeleton) return null;
		if (mainComponent) return mainComponent;

		// Only render the container if we have at least one piece of info
		if (quizName || courseName) {
			return (
				<div
					{...slotProps?.quizInfoContainer}
					className={`${styles.quizInfoContainer} ${slotProps?.quizInfoContainer?.className || ""}`}
				>
					<Typography className={styles.quizLabel}>{i18nPackage.t("beforeQuiz.welcome.quizTitle")}</Typography>
					<CoreSkeletonWrapper loading={!quizName}>
						<Typography className={styles.quizValue}>{quizName}</Typography>
					</CoreSkeletonWrapper>

					<Typography className={styles.quizLabel}>{i18nPackage.t("beforeQuiz.welcome.courseName")}</Typography>
					<CoreSkeletonWrapper loading={!courseName}>
						<Typography className={styles.quizValue}>{courseName}</Typography>
					</CoreSkeletonWrapper>

					{slotProps?.quizInfoContainer?.children}
				</div>
			);
		}

		return null;
	};

	return (
		<div {...slotProps?.container} className={`${styles.welcomeContainer} ${slotProps?.container?.className || ""}`}>
			<div className={styles.verityTitleWrapper}>
				<VerityColorLogo width={42} height={42} />
				<Typography variant="h1" className={styles.verityTitle}>
					{title ?? getBeforeQuizTitle(isCompatibilityCheck)}
				</Typography>
			</div>

			{showDescription && (
				<CoreSkeletonWrapper loading={showSkeleton} variant="text" width={575}>
					<Typography className={styles.welcomeDescription}>
						{i18nPackage.t("beforeQuiz.welcome.description")}
					</Typography>
				</CoreSkeletonWrapper>
			)}

			{showImage && (
				<CoreSkeletonWrapper loading={showSkeleton} width={575} height={250}>
					<div className={styles.imageContainer}>
						<BeforeQuizImage role="presentation" />
					</div>
				</CoreSkeletonWrapper>
			)}

			{showTitle && (
				<CoreSkeletonWrapper loading={showSkeleton} width={575} height={50}>
					<Typography className={styles.welcomeTitle}>{i18nPackage.t("beforeQuiz.welcome.title")}</Typography>
				</CoreSkeletonWrapper>
			)}

			{renderContent()}

			<CoreSkeletonWrapper loading={showSkeleton} width={575} height={35}>
				<CoreButton
					{...slotProps?.startButton}
					className={`${styles.startCheckButton} ${slotProps?.startButton?.className || ""}`}
					disabled={slotProps?.startButton?.disabled ?? !onStart}
					onClick={onStart}
				>
					{i18nPackage.t("beforeQuiz.buttons.startCheck")}
				</CoreButton>
			</CoreSkeletonWrapper>
		</div>
	);
}
